import { Directive, ElementRef, EventEmitter, Input, Output, OnInit, OnDestroy, Renderer2 } from '@angular/core';
import { HttpClient, HttpEventType, HttpHeaders, HttpRequest, HttpResponse } from '@angular/common/http';
import { Subject, Subscription } from 'rxjs';
import {
  UploadFile,
  UploadInput,
  UploadOutput,
  UploadStatus,
  generateId
} from '../_models/primeng-uploads';

/**
 * Directive that replicates ngFileSelect behavior using native file input
 */
@Directive({
  selector: '[ngFileSelect]',
  standalone: false
})
export class NgFileSelectDirective implements OnInit, OnDestroy {
  @Input() uploadInput: EventEmitter<UploadInput> = new EventEmitter<UploadInput>();
  @Output() uploadOutput: EventEmitter<UploadOutput> = new EventEmitter<UploadOutput>();

  private files: UploadFile[] = [];
  private subscriptions: Subscription[] = [];
  private uploadQueue: UploadFile[] = [];

  constructor(
    private el: ElementRef,
    private http: HttpClient,
    private renderer: Renderer2
  ) {}

  ngOnInit() {
    // Listen to file selection
    this.renderer.listen(this.el.nativeElement, 'change', (event: Event) => {
      const target = event.target as HTMLInputElement;
      if (target.files && target.files.length > 0) {
        this.handleFiles(target.files);
      }
    });

    // Listen to upload input commands
    if (this.uploadInput) {
      const sub = this.uploadInput.subscribe((input: UploadInput) => {
        this.handleUploadInput(input);
      });
      this.subscriptions.push(sub);
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  private handleFiles(fileList: FileList) {
    const filesArray = Array.from(fileList);

    filesArray.forEach((nativeFile: File, index: number) => {
      const uploadFile: UploadFile = {
        id: generateId(),
        fileIndex: this.files.length + index,
        lastModifiedDate: new Date(nativeFile.lastModified),
        name: nativeFile.name,
        size: nativeFile.size,
        type: nativeFile.type,
        form: new FormData(),
        progress: {
          status: UploadStatus.Queue,
          data: {
            percentage: 0,
            speed: 0,
            speedHuman: '0 B/s',
            startTime: null,
            endTime: null,
            eta: null,
            etaHuman: null
          }
        },
        nativeFile: nativeFile
      };

      this.files.push(uploadFile);
      this.uploadQueue.push(uploadFile);

      // Emit addedToQueue event
      this.uploadOutput.emit({
        type: 'addedToQueue',
        file: uploadFile,
        nativeFile: nativeFile
      });
    });

    // Emit allAddedToQueue event
    this.uploadOutput.emit({
      type: 'allAddedToQueue'
    });
  }

  private handleUploadInput(input: UploadInput) {
    switch (input.type) {
      case 'uploadAll':
        this.uploadAllFiles(input);
        break;
      case 'uploadFile':
        this.uploadFile(input);
        break;
      case 'cancel':
        this.cancelUpload(input.id);
        break;
      case 'cancelAll':
        this.cancelAllUploads();
        break;
      case 'remove':
        this.removeFile(input.id);
        break;
      case 'removeAll':
        this.removeAllFiles();
        break;
    }
  }

  private uploadAllFiles(input: UploadInput) {
    this.uploadQueue.forEach(file => {
      this.startUpload(file, input);
    });
  }

  private uploadFile(input: UploadInput) {
    const file = this.files.find(f => f.id === input.id);
    if (file) {
      this.startUpload(file, input);
    }
  }

  private startUpload(file: UploadFile, input: UploadInput) {
    if (!input.url || !file.nativeFile) {
      return;
    }

    // Update status to uploading
    file.progress.status = UploadStatus.Uploading;
    file.progress.data!.startTime = Date.now();

    // Emit start event
    this.uploadOutput.emit({
      type: 'start',
      file: file
    });

    // Prepare form data
    const formData = new FormData();
    formData.append(input.fieldName || 'file', file.nativeFile, file.name);

    // Add additional data
    if (input.data) {
      Object.keys(input.data).forEach(key => {
        const value = input.data![key];
        if (value !== null && value !== undefined) {
          formData.append(key, value);
        }
      });
    }

    file.form = formData;

    // Prepare headers
    let headers = new HttpHeaders();
    if (input.headers) {
      Object.keys(input.headers).forEach(key => {
        headers = headers.append(key, input.headers![key]);
      });
    }

    // Create HTTP request
    const req = new HttpRequest(input.method || 'POST', input.url, formData, {
      headers: headers,
      reportProgress: true,
      withCredentials: input.withCredentials || false
    });

    // Execute upload
    const sub = this.http.request(req).subscribe(
      event => {
        if (event.type === HttpEventType.UploadProgress) {
          // Update progress
          const percentDone = event.total ? Math.round((100 * event.loaded) / event.total) : 0;
          file.progress.data!.percentage = percentDone;

          // Emit uploading event
          this.uploadOutput.emit({
            type: 'uploading',
            file: file
          });
        } else if (event instanceof HttpResponse) {
          // Upload complete
          file.progress.status = UploadStatus.Done;
          file.progress.data!.percentage = 100;
          file.progress.data!.endTime = Date.now();
          file.response = event.body;
          file.responseStatus = event.status;

          // Emit done event
          this.uploadOutput.emit({
            type: 'done',
            file: file
          });

          // Remove from queue
          this.uploadQueue = this.uploadQueue.filter(f => f.id !== file.id);
        }
      },
      error => {
        // Handle error
        file.progress.status = UploadStatus.Cancelled;
        file.responseStatus = error.status;

        this.uploadOutput.emit({
          type: 'cancelled',
          file: file
        });

        this.uploadQueue = this.uploadQueue.filter(f => f.id !== file.id);
      }
    );

    file.sub = sub;
  }

  private cancelUpload(id?: string) {
    const file = this.files.find(f => f.id === id);
    if (file && file.sub) {
      file.sub.unsubscribe();
      file.progress.status = UploadStatus.Cancelled;

      this.uploadOutput.emit({
        type: 'cancelled',
        file: file
      });

      this.uploadQueue = this.uploadQueue.filter(f => f.id !== id);
    }
  }

  private cancelAllUploads() {
    this.files.forEach(file => {
      if (file.sub) {
        file.sub.unsubscribe();
        file.progress.status = UploadStatus.Cancelled;
      }
    });
    this.uploadQueue = [];
  }

  private removeFile(id?: string) {
    this.files = this.files.filter(f => f.id !== id);
    this.uploadQueue = this.uploadQueue.filter(f => f.id !== id);

    this.uploadOutput.emit({
      type: 'removed'
    });
  }

  private removeAllFiles() {
    this.files = [];
    this.uploadQueue = [];

    this.uploadOutput.emit({
      type: 'removedAll'
    });

    // Reset file input
    if (this.el.nativeElement) {
      this.el.nativeElement.value = '';
    }
  }
}

/**
 * Directive for drag and drop file upload
 */
@Directive({
  selector: '[ngFileDrop]',
  standalone: false
})
export class NgFileDropDirective implements OnInit, OnDestroy {
  @Input() uploadInput: EventEmitter<UploadInput> = new EventEmitter<UploadInput>();
  /**
   * When false the drop zone stays inert: dropped files are neither queued nor
   * reported, and no drag feedback is emitted. Default drag/drop behaviour is
   * still suppressed so the browser does not navigate to the dropped file.
   * Commands sent through `uploadInput` keep working regardless.
   */
  @Input() dropEnabled = true;
  @Output() uploadOutput: EventEmitter<UploadOutput> = new EventEmitter<UploadOutput>();

  private subscriptions: Subscription[] = [];
  private files: UploadFile[] = [];
  private uploadQueue: UploadFile[] = [];

  constructor(
    private el: ElementRef,
    private http: HttpClient,
    private renderer: Renderer2
  ) {}

  ngOnInit() {
    // Prevent default drag behaviors
    ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
      this.renderer.listen(this.el.nativeElement, eventName, (e: Event) => {
        e.preventDefault();
        e.stopPropagation();
      });
    });

    // Highlight drop area when dragging over it
    ['dragenter', 'dragover'].forEach(eventName => {
      this.renderer.listen(this.el.nativeElement, eventName, () => {
        if (!this.dropEnabled) {
          return;
        }
        this.uploadOutput.emit({ type: 'dragOver' });
      });
    });

    ['dragleave', 'drop'].forEach(eventName => {
      this.renderer.listen(this.el.nativeElement, eventName, () => {
        if (!this.dropEnabled) {
          return;
        }
        this.uploadOutput.emit({ type: 'dragOut' });
      });
    });

    // Handle dropped files
    this.renderer.listen(this.el.nativeElement, 'drop', (e: DragEvent) => {
      if (!this.dropEnabled) {
        return;
      }
      const files = e.dataTransfer?.files;
      if (files && files.length > 0) {
        this.handleFiles(files);
        this.uploadOutput.emit({ type: 'drop' });
      }
    });

    // Listen to upload input commands
    if (this.uploadInput) {
      const sub = this.uploadInput.subscribe((input: UploadInput) => {
        this.handleUploadInput(input);
      });
      this.subscriptions.push(sub);
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  private handleUploadInput(input: UploadInput) {
    switch (input.type) {
      case 'uploadAll':
        this.uploadAllFiles(input);
        break;
      case 'uploadFile':
        this.uploadFile(input);
        break;
      case 'cancel':
        this.cancelUpload(input.id);
        break;
      case 'cancelAll':
        this.cancelAllUploads();
        break;
      case 'remove':
        this.removeFile(input.id);
        break;
      case 'removeAll':
        this.removeAllFiles();
        break;
    }
  }

  private uploadAllFiles(input: UploadInput) {
    this.uploadQueue.forEach(file => {
      this.startUpload(file, input);
    });
  }

  private uploadFile(input: UploadInput) {
    const file = this.files.find(f => f.id === input.id);
    if (file) {
      this.startUpload(file, input);
    }
  }

  private startUpload(file: UploadFile, input: UploadInput) {
    if (!input.url || !file.nativeFile) {
      return;
    }

    // Update status to uploading
    file.progress.status = UploadStatus.Uploading;
    file.progress.data!.startTime = Date.now();

    // Emit start event
    this.uploadOutput.emit({
      type: 'start',
      file: file
    });

    // Prepare form data
    const formData = new FormData();
    formData.append(input.fieldName || 'file', file.nativeFile, file.name);

    // Add additional data
    if (input.data) {
      Object.keys(input.data).forEach(key => {
        const value = input.data![key];
        if (value !== null && value !== undefined) {
          formData.append(key, value);
        }
      });
    }

    file.form = formData;

    // Prepare headers
    let headers = new HttpHeaders();
    if (input.headers) {
      Object.keys(input.headers).forEach(key => {
        headers = headers.append(key, input.headers![key]);
      });
    }

    // Create HTTP request
    const req = new HttpRequest(input.method || 'POST', input.url, formData, {
      headers: headers,
      reportProgress: true,
      withCredentials: input.withCredentials || false
    });

    // Execute upload
    const sub = this.http.request(req).subscribe(
      event => {
        if (event.type === HttpEventType.UploadProgress) {
          // Update progress
          const percentDone = event.total ? Math.round((100 * event.loaded) / event.total) : 0;
          file.progress.data!.percentage = percentDone;

          // Emit uploading event
          this.uploadOutput.emit({
            type: 'uploading',
            file: file
          });
        } else if (event instanceof HttpResponse) {
          // Upload complete
          file.progress.status = UploadStatus.Done;
          file.progress.data!.percentage = 100;
          file.progress.data!.endTime = Date.now();
          file.response = event.body;
          file.responseStatus = event.status;

          // Emit done event
          this.uploadOutput.emit({
            type: 'done',
            file: file
          });

          // Remove from queue
          this.uploadQueue = this.uploadQueue.filter(f => f.id !== file.id);
        }
      },
      error => {
        // Handle error
        file.progress.status = UploadStatus.Cancelled;
        file.responseStatus = error.status;

        this.uploadOutput.emit({
          type: 'cancelled',
          file: file
        });

        this.uploadQueue = this.uploadQueue.filter(f => f.id !== file.id);
      }
    );

    file.sub = sub;
  }

  private cancelUpload(id?: string) {
    const file = this.files.find(f => f.id === id);
    if (file && file.sub) {
      file.sub.unsubscribe();
      file.progress.status = UploadStatus.Cancelled;

      this.uploadOutput.emit({
        type: 'cancelled',
        file: file
      });

      this.uploadQueue = this.uploadQueue.filter(f => f.id !== id);
    }
  }

  private cancelAllUploads() {
    this.files.forEach(file => {
      if (file.sub) {
        file.sub.unsubscribe();
        file.progress.status = UploadStatus.Cancelled;
      }
    });
    this.uploadQueue = [];
  }

  private removeFile(id?: string) {
    this.files = this.files.filter(f => f.id !== id);
    this.uploadQueue = this.uploadQueue.filter(f => f.id !== id);

    this.uploadOutput.emit({
      type: 'removed'
    });
  }

  private removeAllFiles() {
    this.files = [];
    this.uploadQueue = [];

    this.uploadOutput.emit({
      type: 'removedAll'
    });
  }

  private handleFiles(fileList: FileList) {
    const filesArray = Array.from(fileList);

    filesArray.forEach((nativeFile: File, index: number) => {
      const uploadFile: UploadFile = {
        id: generateId(),
        fileIndex: this.files.length + index,
        lastModifiedDate: new Date(nativeFile.lastModified),
        name: nativeFile.name,
        size: nativeFile.size,
        type: nativeFile.type,
        form: new FormData(),
        progress: {
          status: UploadStatus.Queue,
          data: {
            percentage: 0,
            speed: 0,
            speedHuman: '0 B/s',
            startTime: null,
            endTime: null,
            eta: null,
            etaHuman: null
          }
        },
        nativeFile: nativeFile
      };

      this.files.push(uploadFile);
      this.uploadQueue.push(uploadFile);

      this.uploadOutput.emit({
        type: 'addedToQueue',
        file: uploadFile,
        nativeFile: nativeFile
      });
    });

    this.uploadOutput.emit({
      type: 'allAddedToQueue'
    });
  }
}
